import { useState } from "react";
import ReferencePicker from "../EmissionFactorBreakdown/ReferencePicker";
import { saveEnergySource } from "./energyApi";
import { sourceDraftError, sourceDraftToPayload } from "./helpers";

/**
 * Adds an energy source, or edits one, with the publication it is quoted
 * from. The draft is checked before it is sent, and the backend's answer is
 * shown in place when it refuses. It is not a <form>: the new-link form sits
 * inside it, and forms cannot nest.
 *
 * @param {object} props
 * @param {import("./helpers").SourceDraft} props.initial
 * @param {import("./helpers").EnergySource[]} props.sources every source, for the duplicate name check
 * @param {import("../EmissionFactorBreakdown/referencesApi").GhgReference[]} props.references
 * @param {() => void} props.onReferencesChanged
 * @param {(saved: import("./helpers").EnergySource) => void} props.onSaved
 * @param {() => void} props.onCancel
 */
export default function SourceForm({ initial, sources, references, onReferencesChanged, onSaved, onCancel }) {
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const editing = initial.id != null;
  const change = (key) => (e) => {
    const { value } = e.target;
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const submit = async () => {
    const problem = sourceDraftError(draft, sources);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    const res = await saveEnergySource(draft.id, sourceDraftToPayload(draft));
    setSaving(false);
    if (res?.isSuccess && res.data?.data) onSaved(res.data.data);
    else setError(res?.data?.error || "Couldn't save the energy source.");
  };

  return (
    <section className="kgb-card gdb-form" aria-label={editing ? `Edit ${initial.name}` : "Add an energy source"}>
      <header className="gdb-form-head">
        <h2>{editing ? `Edit ${initial.name}` : "Add an energy source"}</h2>
        <p>A set of energy factors. Companies that have not picked a source are priced from the newest one.</p>
      </header>

      <div className="gdb-form-body">
        <div className="gdb-form-grid">
          <label className="gdb-field gdb-field--wide" htmlFor="enf-source-name">
            <span>Name *</span>
            <input id="enf-source-name" type="text" maxLength={256} value={draft.name} placeholder="CEA 2026" onChange={change("name")} />
          </label>

          <label className="gdb-field gdb-field--wide" htmlFor="enf-source-label">
            <span>Source label</span>
            <input
              id="enf-source-label"
              type="text"
              maxLength={512}
              value={draft.source}
              placeholder="IPCC 2006 Guidelines, Vol 2"
              onChange={change("source")}
            />
          </label>

          <label className="gdb-field" htmlFor="enf-source-priority">
            <span>Priority</span>
            <input
              id="enf-source-priority"
              type="number"
              min="1"
              step="1"
              value={draft.priority}
              placeholder="Latest"
              onChange={change("priority")}
            />
            <small>Higher is listed first to companies; blank makes a new source the latest.</small>
          </label>

          <ReferencePicker
            id="enf-source-reference"
            label="Source link"
            value={draft.referenceId}
            references={references}
            onChange={(referenceId) => setDraft((current) => ({ ...current, referenceId }))}
            onReferencesChanged={onReferencesChanged}
          />
        </div>

        {error ? (
          <p className="gdb-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="gdb-form-actions">
          <button type="button" className="kgb-btn kgb-btn--primary" disabled={saving} onClick={submit}>
            {saving ? "Saving…" : editing ? "Save changes" : "Add source"}
          </button>
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}
